import * as z from 'zod/mini';
import { clone, equals } from 'ramda';
import TableViewToggles from '@rio-cloud/rio-uikit/TableViewToggles';
import { DriverStatusInRoute } from './router/types';
import { SortByField } from '../../types';

export const FLEET_DRIVER_GROUP = 'Fleet driver';

export const SORT_ASC = 'asc';
export const SORT_DESC = 'desc';
export const sortDirection = [SORT_ASC, SORT_DESC];

export const INTL_FIELD_FIRST_NAME = 'intl-msg:firstName';
export const INTL_FIELD_LAST_NAME = 'intl-msg:lastName';
export const INTL_FIELD_CARD = 'intl-msg:card';
export const INTL_FIELD_ACTIVE_CARD = 'intl-msg:status.active';
export const INTL_FIELD_STATUS = 'intl-msg:status';
export const INTL_FIELD_EMAIL = 'intl-msg:email';
export const INTL_FIELD_PHONE = 'intl-msg:phoneNumber';
export const INTL_FIELD_TAG = 'intl-msg:tag';
export const INTL_FIELD_USER = 'intl-msg:user';

export const INTL_FIELD_DRIVER_STATUS_ACTIVE = 'intl-msg:status.active';
export const INTL_FIELD_DRIVER_STATUS_ARCHIVED = 'intl-msg:status.archived';

export const sortableFields = Object.values(SortByField);

const stringMaxSchema = (maxLength: number) => z.string().check(z.maxLength(maxLength));
const trimmedStringMaxSchema = (maxLength: number) => z.string().check(z.trim(), z.maxLength(maxLength));
const optionalEmptyOrNullStringSchema = <T>(schema: z.ZodMiniType<T>) =>
    z.optional(z.union([schema, z.literal(''), z.null()]));

export const driversSortFieldNameSchema = z.enum(sortableFields as [string, ...string[]]);

export const LIST_MODE_LIST = TableViewToggles.VIEW_TYPE_SINGLE_CARD;
export const LIST_MODE_CARDS = TableViewToggles.VIEW_TYPE_MULTI_CARDS;
export const LIST_MODE_TABLE = TableViewToggles.VIEW_TYPE_TABLE;

export const listMode = {
    isList: equals(LIST_MODE_LIST),
    isSplit: equals(LIST_MODE_CARDS),
    isTable: equals(LIST_MODE_TABLE),
};

export const driverIdSchema = z.uuid();
export const accountIdSchema = z.string();

export const PROP_DRIVER_ID = 'driverId';
export const PROP_ACCOUNT_ID = 'accountId';
export const PROP_FIRST_NAME = 'firstName';
export const PROP_LAST_NAME = 'lastName';
export const PROP_STATUS = 'status';
export const PROP_ACTIVE_CARD_NUMBER = 'activeCardNumber';
export const PROP_OTHER_CARD_NUMBERS = 'otherCardNumbers';
export const PROP_EMAIL = 'email';
export const PROP_PHONE_NUMBER = 'phoneNumber';
export const PROP_IDENTIFICATION = 'identification';

export const DRIVER_NAME_MAX_LENGTH = 40;

export const driverNameSchema = optionalEmptyOrNullStringSchema(trimmedStringMaxSchema(DRIVER_NAME_MAX_LENGTH));
export const userFirstNameSchema = z.string().check(z.minLength(1), z.maxLength(40));
export const userLastNameSchema = z.string().check(z.trim(), z.minLength(1), z.maxLength(40));
export const driverEmailSchema = optionalEmptyOrNullStringSchema(z.email().check(z.maxLength(73)));
export const driverPhoneNumberSchema = optionalEmptyOrNullStringSchema(
    z.string().check(z.trim(), z.regex(/\+[1-9][0-9]{5,13}/), z.maxLength(15))
);
export const driverStatusInRouteSchema = z.optional(
    z.union([z.enum([DriverStatusInRoute.ACTIVE, DriverStatusInRoute.ARCHIVED, DriverStatusInRoute.ALL]), z.null()])
);
export const driverStatusFilterSchema = z.array(driverStatusInRouteSchema);
export const tagNameSchema = stringMaxSchema(255);

export const driverStatusSchema = z.optional(
    z.union([z.enum([DriverStatusInRoute.ACTIVE, DriverStatusInRoute.ARCHIVED]), z.null()])
);
export const driverSchema = z.looseObject({
    driverId: driverIdSchema,
    accountId: accountIdSchema,
    firstName: driverNameSchema,
    lastName: driverNameSchema,
    language: optionalEmptyOrNullStringSchema(z.string()),
    phoneNumber: driverPhoneNumberSchema,
    email: driverEmailSchema,
    annotation: optionalEmptyOrNullStringSchema(z.string()),
    status: driverStatusSchema,
    driverGroupNames: z.optional(z.array(z.unknown())),
});
export const driverArraySchema = z.array(driverSchema);

// biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
export const configureValidate = (console: Console) => (schema: z.ZodMiniType, value: any) => {
    if (!schema) {
        throw new Error('Please supply a schema to "validate".');
    }

    const result = schema.safeParse(clone(value));
    if (!result.success) {
        console.warn(result.error);
    }
    return result;
};

export const validate = configureValidate(window.console);
